import React, { useMemo } from "react";
import { Select, ButtonGroup, NumberInput } from "@caelor/ui";
import { useNewsStore } from "../../../../../../stores/use-news-store";
import ColorPicker from "../../../../../../components/ui/color-picker/ColorPicker";
import {
  BlogElements,
  BOOLEAN_OPTIONS,
  CORNERS_OPTIONS,
  NewsVariants,
} from "./defaults";
import {
  OptionContainer,
  OptionContent,
  OptionLabel,
  SettingsOptionContainer,
  SettingsOptionHeading,
} from "../../styles";
import { PaginationTypes } from "@Shared/Pagination-Types";
import { useStyleguideColors } from "../../../../../../hooks/use-styleguide-colors";
import { useOpticsStore } from "../../../../../../stores/use-optics-store";
import {
  DEFAULT_LABELS_COLOR,
  DEFAULT_SPACES_COLOR,
} from "../../../../../../features/news/defaults";
import { useTranslation } from "react-i18next";

function LayoutSettings() {
  const { t } = useTranslation();

  const { data, selectedTab, updateConfig, updateConfigTab } = useNewsStore(
    (state) => ({
      data: state.data,
      selectedTab: state.getSelectedTab(),
      updateConfig: state.updateConfig,
      updateConfigTab: state.updateConfigTab,
    })
  );

  const { styleguideSetup } = useOpticsStore((state) => ({
    styleguideSetup: state.styleguideSetup,
  }));

  const styleguideColors = useStyleguideColors({
    styleguideSetup,
  });

  const PaginationOptions = [
    { label: t("none"), value: PaginationTypes.NONE },
    { label: t("show-more"), value: PaginationTypes.INFINITE },
    { label: t("previous-or-next"), value: PaginationTypes.CAROUSEL },
  ];

  const options = useMemo(() => {
    const authorElement = {
      label: t("author"),
      value: BlogElements.AUTHOR,
    };
    const imageElement = {
      label: t("image"),
      value: BlogElements.IMAGE,
    };
    const descriptionElement = {
      label: t("description"),
      value: BlogElements.DESCRIPTION,
    };

    const defaultElements = [
      { label: t("space"), value: BlogElements.SPACE },
      { label: t("labels"), value: BlogElements.LABEL },
      { label: t("date"), value: BlogElements.DATE },
    ];

    if (data.config.variant === NewsVariants.LIST) {
      return [authorElement, imageElement, ...defaultElements];
    }
    if (data.config.variant === NewsVariants.CARD) {
      return [...defaultElements, descriptionElement, authorElement];
    }
    if (data.config.variant === NewsVariants.MAGAZINE) {
      return [authorElement, ...defaultElements, descriptionElement];
    }

    return defaultElements;
  }, [data.config.variant]);

  return (
    <>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("display-options")}</SettingsOptionHeading>

        <OptionContainer>
          <OptionLabel>{t("pagination")}</OptionLabel>
          <OptionContent>
            <Select
              options={PaginationOptions}
              value={PaginationOptions.find(
                ({ value }) => value === data.config.pagination
              )}
              onChange={({ value }) =>
                updateConfig({ value: { pagination: value } })
              }
            />
          </OptionContent>
        </OptionContainer>

        {data.config.variant !== NewsVariants.MAGAZINE && (
          <OptionContainer>
            <OptionLabel>{t("per-page")}</OptionLabel>
            <OptionContent>
              <NumberInput
                value={data.config.tabs[selectedTab].limit}
                setValue={(value) =>
                  updateConfigTab({ value: { limit: value } })
                }
                min={1}
                isFullWidth
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("link")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("new-tab")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={data?.config?.openInNewTab ?? true}
              onClick={(isOn) =>
                updateConfig({
                  value: { openInNewTab: isOn },
                })
              }
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>
      <SettingsOptionContainer>
        <SettingsOptionHeading>
          {t("flexible-display-items")}
        </SettingsOptionHeading>
        {options.map((option) => (
          <OptionContainer key={option.value}>
            <OptionLabel>{option.label}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={BOOLEAN_OPTIONS}
                value={data?.config?.activeElements?.includes(option.value)}
                onClick={(isOn) => {
                  const newElements = isOn
                    ? [...data.config.activeElements, option.value]
                    : data.config.activeElements.filter(
                        (el) => el !== option.value
                      );
                  updateConfig({
                    value: { activeElements: newElements },
                  });
                }}
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        ))}
      </SettingsOptionContainer>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("styling")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("corners")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              i18n={t}
              items={CORNERS_OPTIONS}
              value={data.config.cardBorderRadius}
              options={{ input: true }}
              onClick={(value) =>
                updateConfig({
                  value: { cardBorderRadius: value },
                })
              }
              onChange={({ target: { value } }) => {
                if (value.length < 4) {
                  updateConfig({
                    value: { cardBorderRadius: value },
                  });
                }
              }}
            />
          </OptionContent>
        </OptionContainer>
        <OptionContainer>
          <OptionLabel alignToTop>{t("color")}</OptionLabel>
          <OptionContent>
            <ColorPicker
              hasOpacity={false}
              hideColorTiles
              value={
                data.config.colors || styleguideColors || DEFAULT_LABELS_COLOR
              }
              onChange={(value) => updateConfig({ value: { colors: value } })}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>
    </>
  );
}

export default LayoutSettings;
